import { ALL_FORMATS, Input, UrlSource } from "mediabunny";
import React, { useCallback, useEffect, useState } from "react";
import {
	continueRender,
	delayRender,
	Loop,
	OffthreadVideo,
	useVideoConfig,
} from "remotion";

// Get video duration using mediabunny
async function getVideoDuration(url: string): Promise<number> {
	let input: Input | null = null;
	try {
		input = new Input({
			source: new UrlSource(url),
			formats: ALL_FORMATS,
		});
		const duration = await input.computeDuration();
		return duration ?? 30; // Fallback 30 seconds
	} catch (err) {
		console.error(`Failed to get video duration for ${url}:`, err);
		return 30;
	} finally {
		input?.dispose();
	}
}

export const VideoBackground: React.FC<{
	videoUrl: string;
}> = ({ videoUrl }) => {
	const [durationInSeconds, setDurationInSeconds] = useState<number | null>(
		null,
	);
	const [handle] = useState(() => delayRender());
	const { fps } = useVideoConfig();

	const fetchMetadata = useCallback(async () => {
		try {
			const duration = await getVideoDuration(videoUrl);
			setDurationInSeconds(duration);
			continueRender(handle);
		} catch (err) {
			console.error(`Error fetching metadata for ${videoUrl}:`, err);
			continueRender(handle);
		}
	}, [handle, videoUrl]);

	useEffect(() => {
		fetchMetadata();
	}, [fetchMetadata]);

	if (durationInSeconds === null) {
		return null;
	}

	// Calculate duration in frames, ensure at least 1 frame
	const durationInFrames = Math.max(1, Math.floor(durationInSeconds * fps));

	return (
		<Loop durationInFrames={durationInFrames}>
			<OffthreadVideo
				src={videoUrl}
				style={{
					width: "100%",
					height: "100%",
					objectFit: "cover",
				}}
				muted
			/>
		</Loop>
	);
};
